import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import Button from '../ui/Button';
import { FileText, Wallet, Pencil, Trash2, ArrowUp, ArrowDown, Eye } from 'lucide-react';
import { Client, Supplier } from '../../types';
import InvoiceModal from '../modals/InvoiceModal';
import PaymentModal from '../modals/PaymentModal';
import EntityModal from '../modals/EntityModal';
import ConfirmModal from '../modals/ConfirmModal';
import { useAppStore } from '../../store';
import { supabase } from '../../lib/supabase';

interface EntityTableProps {
  data: Client[] | Supplier[];
  entityType: 'client' | 'supplier';
  onViewDetails: (id: string) => void;
  onEdit?: (entity: Client | Supplier) => void;
}

type SortField = 'name' | 'balance';
type SortDirection = 'asc' | 'desc';

const EntityTable: React.FC<EntityTableProps> = ({
  data,
  entityType,
  onViewDetails,
  onEdit,
}) => {
  const navigate = useNavigate();
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  const { updateClient, deleteClient, updateSupplier, deleteSupplier, setError, calculateEntityBalance } = useAppStore();
  
  const handleViewDetails = (id: string) => {
    if (entityType === 'client') {
      navigate(`/clientes/${id}`);
    } else {
      navigate(`/proveedores/${id}`);
    }
  };
  
  const handleAddInvoice = (id: string) => {
    setSelectedEntity(id);
    setIsInvoiceModalOpen(true);
  };
  
  const handleAddPayment = (id: string) => {
    setSelectedEntity(id);
    setIsPaymentModalOpen(true);
  };

  const handleEdit = (id: string) => {
    setSelectedEntity(id);
    
    // If external edit handler is provided, use it
    if (onEdit) {
      const entity = data.find(e => e.id === id);
      if (entity) {
        onEdit(entity);
      }
    } else {
      // Otherwise, open the edit modal directly
      setIsEditModalOpen(true);
    }
  };

  const handleDeleteClick = (id: string) => {
    setItemToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      if (entityType === 'supplier') {
        // First delete associated sample tasks
        const { error: sampleTasksError } = await supabase
          .from('sample_tasks')
          .delete()
          .eq('supplier_id', itemToDelete);

        if (sampleTasksError) {
          throw new Error(`Error deleting associated sample tasks: ${sampleTasksError.message}`);
        }

        // Then delete associated production tasks
        const { error: productionTasksError } = await supabase
          .from('production_tasks')
          .delete()
          .eq('supplier_id', itemToDelete);

        if (productionTasksError) {
          throw new Error(`Error deleting associated production tasks: ${productionTasksError.message}`);
        }

        // Finally delete the supplier
        await deleteSupplier(itemToDelete);
      } else {
        await deleteClient(itemToDelete);
      }
      
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (error: any) {
      console.error('Error al eliminar:', error);
      setError(error.message || 'Error al eliminar');
    }
  };
  
  const getEntityName = (id: string) => {
    const entity = data.find(e => e.id === id);
    return entity ? entity.name : '';
  };

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // If already sorting by this field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If sorting by a new field, set it and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort the data
  const sortedData = [...data].sort((a, b) => {
    if (sortField === 'name') {
      return sortDirection === 'asc' 
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    } else if (sortField === 'balance') {
      const balanceA = calculateEntityBalance(a.id, entityType);
      const balanceB = calculateEntityBalance(b.id, entityType);
      return sortDirection === 'asc'
        ? balanceA - balanceB
        : balanceB - balanceA;
    }
    return 0;
  });

  // Render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 inline ml-1" /> 
      : <ArrowDown className="h-4 w-4 inline ml-1" />;
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('name')}
              >
                Nombre {renderSortIndicator('name')}
              </th>
              <th 
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('balance')}
              >
                Saldo {renderSortIndicator('balance')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {sortedData.map((entity) => (
              <tr key={entity.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {entity.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                  <span
                    className={
                      calculateEntityBalance(entity.id, entityType) > 0
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : calculateEntityBalance(entity.id, entityType) < 0
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-900 dark:text-gray-300'
                    }
                  >
                    {formatCurrency(calculateEntityBalance(entity.id, entityType))}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                  <div className="flex justify-end space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleViewDetails(entity.id)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Ver
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      icon={<Pencil className="h-4 w-4" />}
                      onClick={() => handleEdit(entity.id)}
                    >
                      Editar
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      icon={<Trash2 className="h-4 w-4" />}
                      onClick={() => handleDeleteClick(entity.id)}
                    >
                      Eliminar
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      icon={<FileText className="h-4 w-4" />}
                      onClick={() => handleAddInvoice(entity.id)}
                    >
                      {entityType === 'client' ? 'Factura' : 'Compra'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      icon={<Wallet className="h-4 w-4" />}
                      onClick={() => handleAddPayment(entity.id)}
                    >
                      Pago
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {selectedEntity && (
        <>
          <InvoiceModal
            isOpen={isInvoiceModalOpen}
            onClose={() => {
              setIsInvoiceModalOpen(false);
              setSelectedEntity(null);
            }}
            entity_id={selectedEntity}
            entity_type={entityType}
            entityName={getEntityName(selectedEntity)}
          />
          
          <PaymentModal
            isOpen={isPaymentModalOpen}
            onClose={() => {
              setIsPaymentModalOpen(false);
              setSelectedEntity(null);
            }}
            entityId={selectedEntity}
            entityName={getEntityName(selectedEntity)}
          />

          <EntityModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedEntity(null);
            }}
            entityType={entityType}
            onSave={async (updatedEntity) => {
              if (entityType === 'client') {
                await updateClient(selectedEntity, updatedEntity);
              } else {
                await updateSupplier(selectedEntity, updatedEntity);
              }
              setIsEditModalOpen(false);
              setSelectedEntity(null);
            }}
            initialData={data.find(e => e.id === selectedEntity) as Client | Supplier}
          />
        </>
      )}

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        title={`Eliminar ${entityType === 'client' ? 'Cliente' : 'Proveedor'}`}
        message={`¿Estás seguro de que deseas eliminar este ${entityType === 'client' ? 'cliente' : 'proveedor'}? Se eliminarán todas sus facturas, pagos y tareas asociadas.`}
        confirmText="Eliminar"
        variant="danger"
      />
    </>
  );
};

export default EntityTable;